import _ from 'lodash';
import { defineStore, storeToRefs } from 'pinia';

import { ISessionEvent } from '@/types';
import { getTextEvents } from '@/common';
import { HITL } from '@/api';

export const useNewEventsStore = defineStore('HITL-New-Events', {
  state: () => {
    return {
      /**
       * These counters indicate that new events happened
       * in the visible sessions while Agent UI was open.
       */
      eventsBySessionId: {} as { [sessionId: string]: number; },
      /**
       * These counters are used to display UI indicators
       * and initial data that is stored on server.
       */
      contentEventsBySessionId: {} as { [sessionId: string]: number; },
      /** A list of session IDs, whose events we are listening */
      subscriptions: [] as string[],
    };
  },
  getters: {
    getNewContentEventsCount: state => {
      return (args: string | string[]) => {
        if (_.isString(args)) {
          return state.contentEventsBySessionId[args] || 0;
        }

        let count = 0;
        for (const sessionId of args) {
          count += state.contentEventsBySessionId[sessionId] || 0;
        }

        return count;
      };
    },
  },
});

export const useNewEventsActions = () => {
  const store = useNewEventsStore();

  /** Data about new content events is saved in the database */
  const initializeNewContentEventsCounters = async (args: {
    sessionIds: string[];
  }) => {
    try {
      const { sessionIds } = args;
      const response = await HITL.sessionEvents.getNewEventsCount({
        sessionIds,
      });

      const events = _.mapValues(response.data, value => value.nEvents);
      store.contentEventsBySessionId = {
        ...store.contentEventsBySessionId,
        ...events,
      };
    } catch (e) {
      console.error('Failed to request new text events!', e);
    }
  };

  /** Mark all text events in session as read */
  const saveReadTextEvent = async (args: {
    sessionId: string;
    events: ISessionEvent[];
  }) => {
    const { sessionId, events } = args;
    const nEvents = store.contentEventsBySessionId[sessionId];
    if (!nEvents) {
      return;
    }

    const event = getTextEvents({ events }).pop();
    if (!event) {
      return;
    }

    try {
      clearCountersBySessionIds({ sessionIds: [sessionId] });
      await HITL.sessionEvents.saveReadEventByUserId({
        sessionId,
        eventId: event.eventId,
        eventTimestamp: event.timestamp,
      });
    } catch (e) {
      console.error('Failed to save read text event!', e);
    }
  };

  const addToNewEventsCount = (args: {
    sessionId: string;
    events: ISessionEvent[];
  }) => {
    const { sessionId, events } = args;

    const nEvents = store.eventsBySessionId[sessionId] || 0;
    store.eventsBySessionId[sessionId] = nEvents + events.length;

    const nTextEvents = store.contentEventsBySessionId[sessionId] || 0;
    const newTextEvents = getTextEvents({ events }).length;
    store.contentEventsBySessionId[sessionId] = nTextEvents + newTextEvents;
  };

  const clearCountersBySessionIds = (args: { sessionIds: string[]; }) => {
    store.eventsBySessionId = _.omit(store.eventsBySessionId, args.sessionIds);
    store.contentEventsBySessionId
      = _.omit(store.contentEventsBySessionId, args.sessionIds);
  };

  return {
    initializeNewContentEventsCounters,
    saveReadTextEvent,
    addToNewEventsCount,
    clearCountersBySessionIds,
  };
};

export const useNewEvents = () => {
  return {
    ...useNewEventsActions(),
    ...storeToRefs(useNewEventsStore()),
  };
};
